import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import {
  LogOut, Users, Award, MapPin,
  CalendarDays, FileText, Home, CreditCard,
  Gamepad2, User,
} from "lucide-react";

export default function Layout({ children }) {
  const { user, isAdmin, isMember, logout } = useAuth();
  const location = useLocation();
  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + "/");

  const memberTabs = [
    { to: "/home", icon: Home, label: "Home", testId: "nav-home" },
    { to: "/events", icon: CalendarDays, label: "Events", testId: "nav-events-member" },
    { to: "/games", icon: Gamepad2, label: "Games", testId: "nav-games" },
    { to: "/pass", icon: CreditCard, label: "Pass", testId: "nav-pass" },
    { to: "/me", icon: User, label: "Profile", testId: "nav-profile" },
  ];

  return (
    <div className="min-h-screen bg-lv-bg pb-16 sm:pb-0">
      {/* Top bar */}
      <nav className="sticky top-0 z-50 bg-[#14100C]/90 backdrop-blur-xl border-b border-lv-border" data-testid="main-nav">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-14 sm:h-16 flex items-center justify-between">
          <Link
            to={isAdmin ? "/admin/members" : "/home"}
            className="font-serif text-lg sm:text-xl text-cream tracking-[0.2em] hover:text-bronze transition-colors"
            data-testid="nav-logo"
          >
            LV PASS
          </Link>

          {/* Desktop: admin navigation */}
          {isAdmin && (
            <div className="hidden sm:flex items-center gap-0.5 sm:gap-1">
              <Link to="/admin/members" data-testid="nav-members" className={`flex items-center gap-1.5 px-3 py-2 text-sm tracking-wider uppercase transition-colors min-h-[44px] ${isActive("/admin/members") ? "text-bronze" : "text-lv-muted hover:text-cream"}`}>
                <Users size={16} /><span>Members</span>
              </Link>
              <Link to="/admin/partners" data-testid="nav-partners" className={`flex items-center gap-1.5 px-3 py-2 text-sm tracking-wider uppercase transition-colors min-h-[44px] ${isActive("/admin/partners") ? "text-bronze" : "text-lv-muted hover:text-cream"}`}>
                <Award size={16} /><span>Partners</span>
              </Link>
              <Link to="/admin/chapters" data-testid="nav-chapters" className={`flex items-center gap-1.5 px-3 py-2 text-sm tracking-wider uppercase transition-colors min-h-[44px] ${isActive("/admin/chapters") ? "text-bronze" : "text-lv-muted hover:text-cream"}`}>
                <MapPin size={16} /><span>Chapters</span>
              </Link>
              <Link to="/admin/events" data-testid="nav-events" className={`flex items-center gap-1.5 px-3 py-2 text-sm tracking-wider uppercase transition-colors min-h-[44px] ${isActive("/admin/events") ? "text-bronze" : "text-lv-muted hover:text-cream"}`}>
                <CalendarDays size={16} /><span>Events</span>
              </Link>
              <Link to="/admin/content" data-testid="nav-content" className={`flex items-center gap-1.5 px-3 py-2 text-sm tracking-wider uppercase transition-colors min-h-[44px] ${isActive("/admin/content") ? "text-bronze" : "text-lv-muted hover:text-cream"}`}>
                <FileText size={16} /><span>Content</span>
              </Link>
              <button onClick={() => logout()} data-testid="nav-logout" className="flex items-center gap-1.5 px-3 py-2 text-sm tracking-wider uppercase text-lv-muted hover:text-cream transition-colors min-h-[44px] ml-2">
                <LogOut size={16} /><span>Sign Out</span>
              </button>
            </div>
          )}

          {/* Desktop: member inline navigation */}
          {isMember && !isAdmin && (
            <div className="hidden sm:flex items-center gap-0.5">
              {memberTabs.map((t) => {
                const Icon = t.icon;
                return (
                  <Link
                    key={t.to}
                    to={t.to}
                    data-testid={t.testId}
                    className={`flex items-center gap-1.5 px-3 py-2 text-sm tracking-wider uppercase transition-colors min-h-[44px] ${
                      isActive(t.to) ? "text-bronze" : "text-lv-muted hover:text-cream"
                    }`}
                  >
                    <Icon size={16} /><span>{t.label}</span>
                  </Link>
                );
              })}
              <button onClick={() => logout()} data-testid="nav-logout" className="flex items-center gap-1.5 px-3 py-2 text-sm tracking-wider uppercase text-lv-muted hover:text-cream transition-colors min-h-[44px] ml-2">
                <LogOut size={16} /><span>Sign Out</span>
              </button>
            </div>
          )}

          {/* Mobile: admin compact icons in top bar */}
          {isAdmin && (
            <div className="flex sm:hidden items-center gap-0.5">
              <Link to="/admin/members" className={`p-2.5 min-h-[44px] min-w-[44px] flex items-center justify-center ${isActive("/admin/members") ? "text-bronze" : "text-lv-muted"}`}><Users size={18} /></Link>
              <Link to="/admin/partners" className={`p-2.5 min-h-[44px] min-w-[44px] flex items-center justify-center ${isActive("/admin/partners") ? "text-bronze" : "text-lv-muted"}`}><Award size={18} /></Link>
              <Link to="/admin/events" className={`p-2.5 min-h-[44px] min-w-[44px] flex items-center justify-center ${isActive("/admin/events") ? "text-bronze" : "text-lv-muted"}`}><CalendarDays size={18} /></Link>
              <button onClick={() => logout()} className="p-2.5 min-h-[44px] min-w-[44px] flex items-center justify-center text-lv-muted"><LogOut size={18} /></button>
            </div>
          )}

          {/* Mobile: only sign out in top bar for members (bottom tab handles nav) */}
          {isMember && !isAdmin && (
            <button onClick={() => logout()} data-testid="nav-logout-mobile" className="flex sm:hidden items-center gap-1.5 px-2.5 py-2 text-xs tracking-wider uppercase text-lv-muted hover:text-cream transition-colors min-h-[44px]">
              <LogOut size={16} />
            </button>
          )}
        </div>
      </nav>

      {/* Main content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {children}
      </main>

      {/* Mobile bottom tab bar — members only */}
      {isMember && !isAdmin && (
        <nav
          className="fixed bottom-0 left-0 right-0 z-50 bg-[#14100C]/95 backdrop-blur-xl border-t border-lv-border sm:hidden"
          data-testid="mobile-tab-bar"
        >
          <div className="flex items-center justify-around h-14">
            {memberTabs.map((t) => {
              const Icon = t.icon;
              const active = isActive(t.to);
              return (
                <Link
                  key={t.to}
                  to={t.to}
                  data-testid={`tab-${t.testId}`}
                  className={`flex flex-col items-center justify-center flex-1 h-full transition-colors ${
                    active ? "text-bronze" : "text-lv-muted/60"
                  }`}
                >
                  <Icon size={18} />
                  <span className="text-[9px] tracking-[0.1em] uppercase mt-0.5">{t.label}</span>
                </Link>
              );
            })}
          </div>
        </nav>
      )}
    </div>
  );
}
